import { SbLink, TranslatedSlug } from '../../types'
import { formatPath } from './internalLinks'
import { toLowercasePath } from './lowercasePath'
import { getTranslatedSlugForPage, stripOutHomeForRootPath } from './translatedSlugs'
import { defaultLanguage, LANGUAGE } from '../../../config/storyblok/languages'
import { Link } from '../context/i18n/interfaces'

/**
 * Shared logic: resolves a slug + translated slugs into a final path
 * with correct language prefix, trailing slash, and home page handling.
 */
function resolveTranslatedPath(
  slug: string,
  translatedSlugs: TranslatedSlug[] | undefined,
  effectiveLanguage: string,
): string {
  const translatedPath = effectiveLanguage === defaultLanguage
    ? formatPath(slug)
    : getTranslatedSlugForPage(effectiveLanguage, translatedSlugs)

  const finalPath = translatedPath || formatPath(slug) || '/'

  // Remove any existing language prefix and leading slashes
  const rawPath = finalPath
    .replace(/^\/(de|en|fr)(\/|$)/, '')
    .replace(/^\/+/, '')
    .replace(/\/+$/, '') // strip trailing slash for uniform handling

  // Non-localized paths (e.g., docs) don't get a language prefix. The
  // documentation site is case-sensitive, so its paths are used as authored.
  const isNonLocalizedPath = rawPath === 'docs' || rawPath.startsWith('docs/')
  if (isNonLocalizedPath) {
    return `/${rawPath}/`
  }

  // Website pages are published under lowercase paths (README, "URL casing")
  const cleanPath = toLowercasePath(rawPath)

  // Home page special cases
  if (cleanPath === 'home' || cleanPath === '') {
    return effectiveLanguage === defaultLanguage ? '/' : `/${effectiveLanguage}/`
  }

  if (effectiveLanguage === defaultLanguage) {
    return `/${cleanPath}/`
  }

  return `/${effectiveLanguage}/${cleanPath}/`
}

/**
 * Resolves the translated URL for a Storyblok blok given its full_slug
 * and translated slugs array.
 */
export function getTranslatedUrlFromBlok(
  fullSlug: string,
  translatedSlugs: TranslatedSlug[],
  currentLanguage: string,
): string {
  const effectiveLanguage = currentLanguage === 'default' ? defaultLanguage : currentLanguage
  return resolveTranslatedPath(fullSlug, translatedSlugs, effectiveLanguage)
}

/**
 * Resolves the translated URL for an SbLink based on link type,
 * the current language, and the pre-built translatedLinks lookup.
 */
export function getTranslatedUrlFromLink(
  link: SbLink,
  currentLanguage: string,
  translatedLinks: Record<string, Link>,
): string | undefined {
  switch (link?.linktype) {
    case 'url': {
      return link?.url
    }
    case 'asset': {
      return formatPath(link?.cached_url)
    }
    case 'email': {
      return `mailto:${link?.cached_url}`
    }
    case 'story':
    default: {
      const currentLinkItem = translatedLinks[link?.id]

      if (!currentLinkItem) return

      const effectiveLanguage = currentLanguage === 'default' ? defaultLanguage : currentLanguage
      const path = resolveTranslatedPath(currentLinkItem.defaultSlug, currentLinkItem.translatedSlugs, effectiveLanguage)

      return link?.anchor ? `${path}#${link?.anchor}` : path
    }
  }
}

/**
 * Resolves the navigation path when changing language.
 * Returns the path to navigate to, or null if no navigation should happen (e.g. 404 page).
 */
export function resolveLanguageChangePath(
  newLanguage: LANGUAGE,
  defaultSlug: string,
  translatedSlugs: TranslatedSlug[] | undefined,
  is404Page?: boolean,
): string | null {
  if (is404Page) {
    return null
  }

  if (newLanguage === LANGUAGE.EN) {
    const defaultPath = stripOutHomeForRootPath(defaultSlug)
    if (!defaultPath || defaultPath === 'home' || defaultPath === '/') {
      return '/'
    }
    return toLowercasePath(defaultPath)
  }

  const translatedPath = translatedSlugs?.find(slug => slug.lang === newLanguage)?.path
  if (translatedPath) {
    const isHomePage = !translatedPath || translatedPath === 'home' || translatedPath === '/'
    if (isHomePage) {
      return `/${newLanguage}`
    }
    return `/${newLanguage}/${toLowercasePath(translatedPath.replace(/^\/+/, ''))}`
  }

  return `/${newLanguage}`
}

/**
 * Resolves the path to navigate to when the detected browser language differs
 * from the page language. Returns null when the page has no translation.
 */
export function resolveDetectedLanguagePath(language: string, translatedSlugs: TranslatedSlug[] | undefined): string | null {
  const translatedPath = translatedSlugs?.find(slug => slug.lang === language)?.path
  if (!translatedPath) {
    return null
  }

  return translatedPath === 'home' ? `/${language}` : `/${language}/${toLowercasePath(translatedPath)}`
}
